// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  powerEstimateConfig,
} from "../core/config.js";
import {
  averageValues,
  extentFromValues,
} from "../core/math.js";
import {
  cylinderKeys,
} from "../core/schema.js";
import {
  pointCylinderValues,
} from "./utils.js";

export function engineWorkPoint(point) {
  const phase = String(point?.phase ?? "");
  if (["Startup", "Shutdown"].includes(phase)) return false;
  const egtAverage = averageValues(pointCylinderValues(point, cylinderKeys("egt")));
  const chtAverage = averageValues(pointCylinderValues(point, cylinderKeys("cht")));
  return (Number.isFinite(egtAverage) && egtAverage > 850)
    || (Number.isFinite(chtAverage) && chtAverage > 240)
    || (Number.isFinite(point?.fuelFlow) && point.fuelFlow > 4)
    || (Number.isFinite(point?.rpm) && point.rpm > 1500)
    || (Number.isFinite(point?.map) && point.map > 16);
}

function computedFuelUsedFromFlow(data = []) {
  let totalGallons = 0;
  let hasInterval = false;
  for (let index = 1; index < data.length; index += 1) {
    const previous = data[index - 1];
    const current = data[index];
    const startSeconds = previous?.elapsedSeconds;
    const endSeconds = current?.elapsedSeconds;
    const startFlow = previous?.fuelFlow;
    const endFlow = current?.fuelFlow;
    if (![startSeconds, endSeconds, startFlow, endFlow].every(Number.isFinite)) continue;
    const deltaSeconds = endSeconds - startSeconds;
    if (!(deltaSeconds > 0)) continue;
    totalGallons += ((startFlow + endFlow) / 2) * (deltaSeconds / 3600);
    hasInterval = true;
  }
  return hasInterval ? totalGallons : null;
}

export function fuelUsedMetric(data = []) {
  const extent = extentFromValues(data.map((point) => point.fuelUsed).filter(Number.isFinite));
  const used = extent
    ? Math.max(extent.max - extent.min, extent.max)
    : computedFuelUsedFromFlow(data);
  if (!Number.isFinite(used)) return "Not logged";
  return `${used.toFixed(1)} gal`;
}

export function estimatePower(point, config = powerEstimateConfig) {
  const fuelFlow = point?.fuelFlow;
  if (!Number.isFinite(fuelFlow) || fuelFlow < config.minFuelFlowGph) return null;
  const horsepower = fuelFlow * config.fuelDensityLbPerGal / config.bsfcLbPerHpHour;
  const rpm = point?.rpm;
  const torque = Number.isFinite(rpm) && rpm >= config.minTorqueRpm
    ? horsepower * 5252 / rpm
    : null;
  return {
    horsepower,
    torque,
    basis: "100LL",
  };
}

export function powerEstimateRows(data = [], scope = "work", {
  engineWorkPoint: workPoint = engineWorkPoint,
  estimatePower: powerEstimator = estimatePower,
} = {}) {
  const scoped = scope === "cruise"
    ? data.filter((point) => point.phase === "Cruise")
    : data.filter(workPoint);
  return scoped
    .map((point) => ({ point, estimate: powerEstimator(point) }))
    .filter((row) => row.estimate);
}

export function powerEstimateSummary(data = [], scope = "work", options = {}) {
  const rows = powerEstimateRows(data, scope, options);
  if (!rows.length) return null;
  const hpValues = rows.map((row) => row.estimate.horsepower).filter(Number.isFinite);
  const torqueValues = rows.map((row) => row.estimate.torque).filter(Number.isFinite);
  return {
    avgHp: averageValues(hpValues),
    peakHp: extentFromValues(hpValues)?.max ?? null,
    avgTorque: averageValues(torqueValues),
    peakTorque: extentFromValues(torqueValues)?.max ?? null,
    basis: rows[0]?.estimate.basis ?? "Fuel flow estimate",
  };
}

export function preferredPowerEstimateSummary(data = [], {
  powerEstimateSummary: summarizePower = powerEstimateSummary,
} = {}) {
  const cruise = summarizePower(data, "cruise");
  if (cruise) {
    return {
      ...cruise,
      scope: "cruise",
      cardLabel: "Cruise Power",
      hpLabel: "Estimated cruise HP",
      torqueLabel: "Estimated cruise torque",
      shortHpLabel: "Est. Cruise HP",
      shortTorqueLabel: "Est. Cruise Torque",
    };
  }
  const work = summarizePower(data, "work");
  if (!work) return null;
  return {
    ...work,
    scope: "work",
    cardLabel: "Avg Power",
    hpLabel: "Estimated avg HP",
    torqueLabel: "Estimated avg torque",
    shortHpLabel: "Est. Avg HP",
    shortTorqueLabel: "Est. Avg Torque",
  };
}

export function estimatedHpMetric(data = [], scope = "work", {
  powerEstimateSummary: summarizePower = powerEstimateSummary,
} = {}) {
  const summary = summarizePower(data, scope) ?? (scope === "cruise" ? summarizePower(data, "work") : null);
  return Number.isFinite(summary?.avgHp) ? `${Math.round(summary.avgHp)} hp` : "Not logged";
}

export function estimatedTorqueMetric(data = [], scope = "work", {
  powerEstimateSummary: summarizePower = powerEstimateSummary,
} = {}) {
  const summary = summarizePower(data, scope) ?? (scope === "cruise" ? summarizePower(data, "work") : null);
  return Number.isFinite(summary?.avgTorque) ? `${Math.round(summary.avgTorque)} lb-ft` : "Not logged";
}
